(() => {
  if (customElements.get('tb-pack-turntable')) return;
  const TAU = Math.PI * 2;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const hover = matchMedia('(hover: hover) and (pointer: fine)');
  const vertexSource = `
    attribute vec3 aPosition;
    attribute vec3 aNormal;
    attribute vec2 aUV;
    uniform vec2 uResolution;
    uniform vec2 uCenter;
    uniform float uScale;
    uniform float uAngle;
    uniform float uTilt;
    varying vec3 vNormal;
    varying vec3 vLocal;
    varying vec2 vUV;
    void main() {
      float c = cos(uAngle), s = sin(uAngle), ct = cos(uTilt), st = sin(uTilt);
      mat3 turn = mat3(c,0.,-s, 0.,1.,0., s,0.,c);
      mat3 tilt = mat3(ct,st,0., -st,ct,0., 0.,0.,1.);
      vec3 p = tilt * turn * aPosition;
      float perspective = 9. / (9. - p.z);
      vec2 pixel = uCenter + vec2(p.x,-p.y) * uScale * perspective;
      gl_Position = vec4(pixel.x/uResolution.x*2.-1., 1.-pixel.y/uResolution.y*2., -p.z*.1, 1.);
      vNormal = tilt * turn * aNormal;
      vLocal = aPosition;
      vUV = aUV;
    }
  `;
  const fragmentSource = `
    precision mediump float;
    uniform sampler2D uTexture;
    uniform float uFace;
    uniform vec3 uSilver;
    uniform vec3 uInk;
    varying vec3 vNormal;
    varying vec3 vLocal;
    varying vec2 vUV;
    void main() {
      if (uFace > 1.5) {
        float shadow = exp(-dot(vUV*2.-1.,vUV*2.-1.)*3.)*.18;
        gl_FragColor = vec4(uInk,shadow);
        return;
      }
      vec3 N = normalize(vNormal);
      vec3 light = normalize(vec3(-.8,1.3,2.8));
      float diffuse = max(dot(N,light),0.);
      float strip = pow(max(dot(N,normalize(vec3(-1.2,.2,3.))),0.),22.);
      float sheen = pow(max(dot(N,normalize(vec3(1.5,.5,3.))),0.),45.);
      vec3 color;
      if (uFace < .5) {
        color = texture2D(uTexture,vUV).rgb * (.80 + .20*diffuse);
        color += vec3(strip*.05 + sheen*.06);
      } else {
        float fold = sin(vLocal.x*19.+sin(vLocal.y*8.)*.8)*.018;
        fold += sin(vLocal.y*49.+vLocal.x*11.)*.005;
        float crimp = step(1.20,abs(vLocal.y)) * (sin(vLocal.x*260.)*.025-.05);
        float seam = (1.-smoothstep(.013,.027,abs(vLocal.x)))*.10;
        color = uSilver*(.48 + diffuse*.47 + strip*.43 + sheen*.45 + fold + crimp - seam);
      }
      gl_FragColor = vec4(clamp(color,0.,1.),1.);
    }
  `;
  function point(u, v, face) {
    const rounding = v < .035 ? .045*(1-v/.035)**2 : v > .965 ? .035*((v-.965)/.035)**2 : 0;
    const width = .9-.065*v-rounding;
    const puff = Math.pow(Math.max(0,Math.sin(Math.PI*u)),.5) * Math.pow(Math.max(0,Math.sin(Math.PI*v)),.34);
    const zip = v < .145 ? .30 : 1;
    return [(u*2-1)*width,(.5-v)*2.8,face*(.008+.20*puff*zip)];
  }
  function mesh(face) {
    const data = [], nx = 32, ny = 38;
    function vertex(u,v) {
      const p = point(u,v,face), epsilon = .0001;
      const l = point(Math.max(0,u-epsilon),v,face), r = point(Math.min(1,u+epsilon),v,face);
      const t = point(u,Math.max(0,v-epsilon),face), b = point(u,Math.min(1,v+epsilon),face);
      const dx = r.map((x,i)=>x-l[i]), dy = t.map((x,i)=>x-b[i]);
      let n = [dx[1]*dy[2]-dx[2]*dy[1],dx[2]*dy[0]-dx[0]*dy[2],dx[0]*dy[1]-dx[1]*dy[0]];
      const length = Math.hypot(...n)||1;
      n = n.map(x=>x/length*face);
      const xInset = .105+.046*v;
      data.push(...p,...n,xInset+u*(.79-.092*v),.08+v*.832);
    }
    for(let y=0;y<ny;y++) for(let x=0;x<nx;x++) {
      const u=x/nx,v=y/ny,du=1/nx,dv=1/ny;
      vertex(u,v);vertex(u+du,v);vertex(u,v+dv);
      vertex(u+du,v);vertex(u+du,v+dv);vertex(u,v+dv);
    }
    return new Float32Array(data);
  }
  let geometry;
  const shadowGeometry = new Float32Array([
    -1,-.15,0,0,0,1,0,0, 1,-.15,0,0,0,1,1,0, -1,.15,0,0,0,1,0,1,
    1,-.15,0,0,0,1,1,0, 1,.15,0,0,0,1,1,1, -1,.15,0,0,0,1,0,1
  ]);
  class PackTurntable extends HTMLElement {
    connectedCallback() {
      if(this.controller) return;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.canvas = this.querySelector('canvas');
      this.button = this.querySelector('button');
      this.button.hidden = reduced.matches;
      this.addEventListener('pointerenter',e=>{if(e.pointerType==='mouse'&&hover.matches)this.play();},options);
      this.button.addEventListener('click',()=>this.play(),options);
      reduced.addEventListener('change',()=>{this.stop();this.button.hidden=reduced.matches;},options);
      document.addEventListener('visibilitychange',()=>{if(document.hidden)this.stop();},options);
      document.addEventListener('tb:dialog-change',()=>{if(document.querySelector('dialog[open]'))this.stop();},options);
      this.observer = new IntersectionObserver(entries=>{this.inView=entries[0].isIntersecting;if(!this.inView)this.stop();});
      this.observer.observe(this);
      this.canvas.addEventListener('webglcontextlost',()=>{this.stop();this.failed=true;this.button.hidden=true;},options);
    }
    disconnectedCallback() {
      this.stop();this.observer?.disconnect();this.controller?.abort();this.controller=null;
      if(this.gl) {
        this.buffers?.forEach(b=>this.gl.deleteBuffer(b));
        this.gl.deleteTexture(this.texture);this.gl.deleteProgram(this.program);
        this.gl.getExtension('WEBGL_lose_context')?.loseContext();this.gl=null;
      }
      this.setupPromise=null;
    }
    async setup() {
      const image = this.querySelector('img');
      if(!image.complete) await image.decode();
      const textureImage = new Image();
      textureImage.crossOrigin = 'anonymous';textureImage.src = image.currentSrc || image.src;
      await textureImage.decode();
      if(!this.isConnected) return false;
      const gl = this.canvas.getContext('webgl',{alpha:true,antialias:true,premultipliedAlpha:false,powerPreference:'low-power'});
      if(!gl) throw new Error('WebGL unavailable');
      this.gl=gl;
      const compile=(type,source)=>{const shader=gl.createShader(type);gl.shaderSource(shader,source);gl.compileShader(shader);if(!gl.getShaderParameter(shader,gl.COMPILE_STATUS))throw new Error('Shader compilation failed');return shader;};
      const vertex=compile(gl.VERTEX_SHADER,vertexSource),fragment=compile(gl.FRAGMENT_SHADER,fragmentSource);
      this.program=gl.createProgram();gl.attachShader(this.program,vertex);gl.attachShader(this.program,fragment);gl.linkProgram(this.program);
      gl.deleteShader(vertex);gl.deleteShader(fragment);
      if(!gl.getProgramParameter(this.program,gl.LINK_STATUS))throw new Error('Shader linking failed');
      gl.useProgram(this.program);
      this.uniforms=Object.fromEntries(['uResolution','uCenter','uScale','uAngle','uTilt','uFace','uSilver','uInk','uTexture'].map(name=>[name,gl.getUniformLocation(this.program,name)]));
      this.attributeLocations=['aPosition','aNormal','aUV'].map(name=>gl.getAttribLocation(this.program,name));
      geometry ||= [mesh(1),mesh(-1)];
      this.buffers=[...geometry,shadowGeometry].map(data=>{const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,data,gl.STATIC_DRAW);return buffer;});
      this.texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,this.texture);
      gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,textureImage);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
      gl.enable(gl.DEPTH_TEST);gl.enable(gl.BLEND);gl.blendFunc(gl.SRC_ALPHA,gl.ONE_MINUS_SRC_ALPHA);
      return true;
    }
    layout() {
      const box=this.getBoundingClientRect(),ratio=Math.min(devicePixelRatio||1,2);
      this.canvas.width=Math.round(box.width*ratio);this.canvas.height=Math.round(box.height*ratio);
      this.gl.viewport(0,0,this.canvas.width,this.canvas.height);
      this.gl.uniform2f(this.uniforms.uResolution,box.width,box.height);
      const styles=getComputedStyle(this);
      const color=name=>{
        let value=styles.getPropertyValue(name).trim();
        if(value.startsWith('#')) {
          value=value.slice(1);if(value.length===3)value=[...value].map(x=>x+x).join('');
          return [0,2,4].map(i=>parseInt(value.slice(i,i+2),16)/255);
        }
        return (value.match(/[\d.]+/g)||[192,195,200]).slice(0,3).map(x=>Number(x)/255);
      };
      this.gl.uniform3fv(this.uniforms.uSilver,color('--tb-silver'));
      this.gl.uniform3fv(this.uniforms.uInk,color('--tb-ink'));
      this.models=[...this.querySelectorAll('img')].map(image=>{
        const rect=image.getBoundingClientRect();
        const transform=new DOMMatrixReadOnly(getComputedStyle(image).transform);
        const scale=Math.hypot(transform.a,transform.b);
        const renderedHeight=Math.min(image.clientHeight,image.clientWidth*954/640)*scale;
        return {x:rect.left+rect.width/2-box.left,y:rect.top+rect.height/2-box.top-.004*renderedHeight,scale:renderedHeight*.832/2.8,tilt:-Math.atan2(transform.b,transform.a)};
      });
      if(this.models.length===3)this.models=[this.models[0],this.models[2],this.models[1]];
    }
    draw(angle) {
      const gl=this.gl,u=this.uniforms;
      gl.clearColor(0,0,0,0);gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);
      gl.useProgram(this.program);
      const drawBuffer=(index,face,count)=>{
        gl.bindBuffer(gl.ARRAY_BUFFER,this.buffers[index]);
        this.attributeLocations.forEach((attribute,i)=>{gl.enableVertexAttribArray(attribute);gl.vertexAttribPointer(attribute,i===2?2:3,gl.FLOAT,false,32,i===0?0:i===1?12:24);});
        gl.uniform1f(u.uFace,face);gl.drawArrays(gl.TRIANGLES,0,count);
      };
      for(const model of this.models) {
        gl.clear(gl.DEPTH_BUFFER_BIT);
        gl.uniform2f(u.uCenter,model.x,model.y+model.scale*1.42);gl.uniform1f(u.uScale,model.scale);gl.uniform1f(u.uAngle,0);gl.uniform1f(u.uTilt,0);
        gl.depthMask(false);drawBuffer(2,2,6);gl.depthMask(true);
        gl.uniform2f(u.uCenter,model.x,model.y);gl.uniform1f(u.uAngle,angle);gl.uniform1f(u.uTilt,model.tilt);
        drawBuffer(0,0,geometry[0].length/8);drawBuffer(1,1,geometry[1].length/8);
      }
    }
    async play() {
      if(this.playing||this.failed||reduced.matches||document.hidden||this.inView===false||document.querySelector('dialog[open]'))return;
      this.playing=true;this.button.setAttribute('aria-busy','true');
      const run=this.run=(this.run||0)+1;
      try {
        this.setupPromise ||= this.setup();
        if(!await this.setupPromise||!this.playing||!this.isConnected||run!==this.run)return;
        this.layout();this.draw(0);this.classList.add('is-turning');
        const started=performance.now();
        const frame=now=>{
          if(!this.playing||run!==this.run)return;
          const progress=Math.min(1,(now-started)/2400);
          const eased=progress*progress*(3-2*progress);
          this.draw(TAU*eased);
          if(progress<1)this.frame=requestAnimationFrame(frame);else this.stop();
        };
        this.frame=requestAnimationFrame(frame);
      } catch (error) {this.failed=true;this.stop();this.button.hidden=true;console.warn('TrueBatch product turntable:',error.message);}
    }
    stop() {
      this.run=(this.run||0)+1;
      cancelAnimationFrame(this.frame);this.playing=false;this.classList.remove('is-turning');this.button?.removeAttribute('aria-busy');
    }
  }
  customElements.define('tb-pack-turntable',PackTurntable);
})();
